import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Form, Button, Alert, Spinner, Badge, Table, Modal, Pagination } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

interface WithdrawalRequest {
  _id: string;
  amount: number;
  bankDetails: {
    accountNumber: string;
    ifscCode: string;
    bankName: string;
    accountHolderName: string;
  };
  status: 'pending' | 'approved' | 'rejected' | 'processed';
  requestedAt: string;
  processedAt?: string;
  adminResponse?: string;
  transactionId?: string;
}

const WithdrawalRequests: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(2);
  const [paginatedRequests, setPaginatedRequests] = useState<WithdrawalRequest[]>([]);
    useEffect(() => {
      setCurrentPage(1);
    }, [requests.length]);

    useEffect(() => {
      const startIdx = (currentPage - 1) * pageSize;
      const endIdx = startIdx + pageSize;
      setPaginatedRequests(requests.slice(startIdx, endIdx));
    }, [requests, currentPage, pageSize]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  
  const [withdrawalAmount, setWithdrawalAmount] = useState<number>(100);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  // Available balance (mock - should come from user context or API)
  const availableBalance = user?.rewardBalance || 0;

  const loadWithdrawalRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/withdrawals/my-requests`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setRequests(response.data.requests || []);
    } catch (error: any) {
      console.error('Failed to load withdrawal requests:', error);
      setError('No Withdrawal History yet');
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    loadWithdrawalRequests();
  }, [loadWithdrawalRequests]);

  const validateWithdrawal = (): string | null => {
    if (withdrawalAmount < 100) {
      return 'Minimum withdrawal amount is ₹100';
    }

    if (withdrawalAmount % 100 !== 0) {
      return 'Withdrawal amount must be in multiples of ₹100';
    }

    if (withdrawalAmount > availableBalance) {
      return 'Insufficient balance for withdrawal';
    }

    if (!user?.bankDetails?.accountNumber) {
      return 'Please complete your bank details in profile before requesting withdrawal';
    }

    if (!user?.kycDetails?.panNumber) {
      return 'Please complete KYC verification before requesting withdrawal';
    }

    return null;
  };

  const handleWithdrawalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateWithdrawal();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API_BASE_URL}/withdrawals/request`, {
        amount: withdrawalAmount
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setSuccess('Withdrawal request submitted successfully! It will be processed within 3-5 business days.');
        setShowModal(false);
        setWithdrawalAmount(100);
        loadWithdrawalRequests(); // Reload the requests
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to submit withdrawal request';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      pending: 'warning',
      approved: 'info',
      processed: 'success',
      rejected: 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  const canRequestWithdrawal = () => {
    return availableBalance >= 100 && 
           user?.bankDetails?.accountNumber && 
           user?.kycDetails?.panNumber &&
           !requests.some(req => req.status === 'pending');
  };

  return (
    <div className="container-fluid py-4">
      <Row>
        <Col lg={10} className="mx-auto">
          {/* Header */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
            <div className="mb-3 mb-md-0">
              <h2 className="mb-1">
                <i className="fas fa-money-bill-wave text-success me-2"></i>
                Withdrawal Requests
              </h2>
              <p className="text-muted mb-0">
                Request withdrawals from your available balance
              </p>
            </div>
            <div className="text-start text-md-end">
              <div className="mb-2">
                <h4 className="text-success mb-0">₹{availableBalance.toFixed(2)}</h4>
                <small className="text-muted">Available Balance</small>
              </div>
              {canRequestWithdrawal() && (
                <Button 
                  variant="success" 
                  onClick={() => setShowModal(true)}
                  disabled={submitting}
                >
                  <i className="fas fa-plus me-2"></i>
                  Request Withdrawal
                </Button>
              )}
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              <i className="fas fa-check-circle me-2"></i>
              {success}
            </Alert>
          )}

          {/* Requirements Card */}
          {!canRequestWithdrawal() && (
            <Card className="mb-4 border-warning">
              <Card.Header className="bg-warning bg-opacity-10 border-warning">
                <h6 className="mb-0 text-warning">
                  <i className="fas fa-info-circle me-2"></i>
                  Requirements for Withdrawal
                </h6>
              </Card.Header>
              <Card.Body>
                <ul className="mb-0">
                  <li className={availableBalance >= 100 ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${availableBalance >= 100 ? 'fa-check' : 'fa-times'} me-2`}></i>
                    Minimum balance of ₹100 (Current: ₹{availableBalance})
                  </li>
                  <li className={user?.bankDetails?.accountNumber ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${user?.bankDetails?.accountNumber ? 'fa-check' : 'fa-times'} me-2`}></i>
                    Complete bank details in profile
                  </li>
                  <li className={user?.kycDetails?.panNumber ? 'text-success' : 'text-danger'}>
                    <i className={`fas ${user?.kycDetails?.panNumber ? 'fa-check' : 'fa-times'} me-2`}></i>
                    Complete KYC verification
                  </li>
                  <li className={!requests.some(req => req.status === 'pending') ? 'text-success' : 'text-warning'}>
                    <i className={`fas ${!requests.some(req => req.status === 'pending') ? 'fa-check' : 'fa-clock'} me-2`}></i>
                    No pending withdrawal requests
                  </li>
                </ul>
                {!user?.bankDetails?.accountNumber && (
                  <div className="mt-3">
                    <Button variant="outline-primary" href="/app/profile">
                      <i className="fas fa-user-edit me-2"></i>
                      Complete Profile
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Withdrawal History */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-history me-2"></i>
                Withdrawal History
              </h5>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                  <p className="mt-2">Loading withdrawal history...</p>
                </div>
              ) : requests.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                  <p className="text-muted">No withdrawal requests found</p>
                  <small className="text-muted">Your withdrawal history will appear here</small>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Amount</th>
                        <th>Bank Details</th>
                        <th>Status</th>
                        <th>Requested On</th>
                        <th>Transaction ID</th>
                        <th>Admin Response</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRequests.map(request => (
                        <tr key={request._id}>
                          <td>
                            <strong className="text-success">₹{request.amount.toLocaleString()}</strong>
                          </td>
                          <td>
                            <div>
                              <small className="d-block">{request.bankDetails?.accountHolderName || 'N/A'}</small>
                              <small className="text-muted">
                                {request.bankDetails?.bankName || 'N/A'}
                              </small>
                              <br />
                              <code className="small">
                                {request.bankDetails?.accountNumber
                                  ? request.bankDetails.accountNumber.replace(/(\d{4})/g, '$1 ')
                                  : 'N/A'}
                              </code>
                            </div>
                          </td>
                          <td>{getStatusBadge(request.status)}</td>
                          <td>
                            <small>{new Date(request.requestedAt).toLocaleString()}</small>
                          </td>
                          <td>
                            {request.transactionId ? (
                              <code className="small text-success">{request.transactionId}</code>
                            ) : (
                              <small className="text-muted">-</small>
                            )}
                          </td>
                          <td>
                            {request.adminResponse ? (
                              <small>{request.adminResponse}</small>
                            ) : (
                              <small className="text-muted">-</small>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {/* Pagination Controls (always visible, now outside tbody) */}
                  <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                    <div>
                      <Form.Select
                        style={{ width: 120, display: 'inline-block' }}
                        value={pageSize}
                        onChange={e => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        size="sm"
                      >
                        {[5, 10, 20, 50, 100].map(size => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </Form.Select>
                    </div>
                    <div>
                      <Pagination className="mb-0">
                        <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                        {(() => {
                          const totalPages = Math.max(1, Math.ceil(requests.length / pageSize));
                          let start = Math.max(1, currentPage - 2);
                          let end = Math.min(totalPages, currentPage + 2);
                          if (end - start < 4) {
                            if (start === 1) end = Math.min(totalPages, start + 4);
                            if (end === totalPages) start = Math.max(1, end - 4);
                          }
                          return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(page => (
                            <Pagination.Item
                              key={page}
                              active={page === currentPage}
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </Pagination.Item>
                          ));
                        })()}
                        <Pagination.Next onClick={() => setCurrentPage(p => Math.min(Math.max(1, Math.ceil(requests.length / pageSize)), p + 1))} disabled={currentPage === Math.max(1, Math.ceil(requests.length / pageSize)) || requests.length === 0} />
                        <Pagination.Last onClick={() => setCurrentPage(Math.max(1, Math.ceil(requests.length / pageSize)))} disabled={currentPage === Math.max(1, Math.ceil(requests.length / pageSize)) || requests.length === 0} />
                      </Pagination>
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Withdrawal Request Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-money-bill-wave me-2"></i>
            Request Withdrawal
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleWithdrawalSubmit}>
          <Modal.Body>
            <Alert variant="info" className="mb-4">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Withdrawal Terms:</strong>
              <ul className="mb-0 mt-2">
                <li>Minimum withdrawal amount: ₹100</li>
                <li>Amount must be in multiples of ₹100</li>
                <li>Processing time: 3-5 business days</li>
                <li>Withdrawal will be processed to your verified bank account</li>
              </ul>
            </Alert>

            <Form.Group className="mb-4">
              <Form.Label>
                <strong>Withdrawal Amount *</strong>
                <small className="text-muted ms-2">(Available: ₹{availableBalance.toLocaleString()})</small>
              </Form.Label>
              <Form.Control
                type="number"
                min="100"
                step="100"
                max={availableBalance}
                value={withdrawalAmount}
                onChange={(e) => setWithdrawalAmount(parseInt(e.target.value) || 0)}
                placeholder="Enter amount (minimum ₹100)"
                required
                disabled={submitting}
              />
              <Form.Text className="text-muted">
                Amount must be in multiples of ₹100
              </Form.Text>
            </Form.Group>

            {/* Quick Amount Buttons */}
            <div className="mb-4">
              <Form.Label className="small text-muted">Quick Select:</Form.Label>
              <div>
                {[100, 500, 1000, 2000, 5000].filter(amt => amt <= availableBalance).map(amount => (
                  <Button
                    key={amount}
                    variant="outline-primary"
                    size="sm"
                    className="me-2 mb-2"
                    onClick={() => setWithdrawalAmount(amount)}
                    disabled={submitting}
                  >
                    ₹{amount.toLocaleString()}
                  </Button>
                ))}
                {availableBalance >= 100 && (
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="me-2 mb-2"
                    onClick={() => setWithdrawalAmount(Math.floor(availableBalance / 100) * 100)}
                    disabled={submitting}
                  >
                    Max: ₹{(Math.floor(availableBalance / 100) * 100).toLocaleString()}
                  </Button>
                )}
              </div>
            </div>

            {/* Bank Details Summary */}
            <Card className="bg-light border-0">
              <Card.Body className="py-3">
                <h6 className="mb-2">Withdrawal will be processed to:</h6>
                <div>
                  <strong>{user?.bankDetails?.accountHolderName}</strong>
                  <br />
                  <small className="text-muted">
                    {user?.bankDetails?.bankName} • {user?.bankDetails?.ifscCode}
                  </small>
                  <br />
                  <code className="small">
                    {user?.bankDetails?.accountNumber?.replace(/(\d{4})/g, '$1 ')}
                  </code>
                </div>
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="success" 
              disabled={submitting || !withdrawalAmount || validateWithdrawal() !== null}
            >
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane me-2"></i>
                  Submit Request (₹{withdrawalAmount.toLocaleString()})
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default WithdrawalRequests;